import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BackLink } from "@/components/layout/BackLink";
import { Button, Dialog, Spinner, Tabs, Tag } from "@/components/ui";
import { useCloseOrder, useOrder } from "@/lib/api/queries";
import { copyText } from "@/lib/clipboard";
import { formatOrderText, groupByMenu, totalPrice, type OrderEntry } from "@/lib/order";
import { useCurrentWorkspace } from "@/stores/workspace";

export default function OrderResultPage() {
  const { orderId = "" } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspace().workspace?.id ?? "";
  const { data: detail, isLoading } = useOrder(workspaceId, orderId);
  const closeOrder = useCloseOrder(workspaceId);
  const [tab, setTab] = useState("menu");
  const [confirmClose, setConfirmClose] = useState(false);

  if (isLoading || !detail) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const entries: OrderEntry[] = detail.entries.map((e) => ({
    personName: e.person_name,
    submitted: e.submitted,
    menuName: e.menu_name,
    optionNames: e.option_names,
    note: e.note,
    price: e.price,
    isCustom: e.is_custom,
  }));

  const customCount = entries.filter((e) => e.submitted && e.isCustom).length;

  const groups = groupByMenu(entries);
  const total = totalPrice(entries);

  async function handleCopyText() {
    if (!detail) return;
    const ok = await copyText(formatOrderText(detail.menuboard_snapshot.name, entries));
    if (ok) toast.success("텍스트를 복사했어요");
    else toast.error("복사하지 못했어요");
  }

  function handleClose() {
    closeOrder.mutate(orderId, {
      onSuccess: () => {
        toast.success("마감했어요");
        setConfirmClose(false);
      },
      onError: () => {
        toast.error("마감하지 못했어요. 다시 시도해 주세요.");
        setConfirmClose(false);
      },
    });
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <BackLink label="목록으로" onClick={() => navigate("/")} />
      <h1 className="mt-2 text-heading-1 font-bold text-label-normal">{detail.menuboard_name}</h1>
      <p className="mt-1 text-body-2 text-label-alternative">
        {detail.submitted_count} / {detail.person_count}명 제출
      </p>

      <div className="mt-4">
        <Tabs
          items={[
            { value: "menu", label: "메뉴별" },
            { value: "person", label: "사람별" },
          ]}
          value={tab}
          onChange={setTab}
          fill
        />
      </div>

      <div className="mt-4">
        {tab === "menu" ? (
          groups.length === 0 ? (
            <p className="text-body-2 text-label-alternative">아직 제출된 주문이 없어요.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {groups.map((group) => (
                <li key={group.label} className="flex items-center justify-between gap-3">
                  <span className="text-body-2 text-label-normal">{group.label}</span>
                  <span className="text-right text-label-1 text-label-alternative">
                    {group.count}개 · {group.personNames.join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : (
          <ul className="flex flex-col gap-3">
            {entries.map((entry, i) => (
              <li key={`${entry.personName}-${i}`} className="flex items-center justify-between">
                <span className="text-body-2 text-label-normal">{entry.personName}</span>
                {entry.submitted ? (
                  <span className="flex items-center gap-1 text-label-1 text-label-alternative">
                    {entry.isCustom && <Tag color="primary">직접 입력</Tag>}
                    {entry.menuName}
                    {entry.optionNames.length > 0 ? ` (${entry.optionNames.join(", ")})` : ""}
                  </span>
                ) : (
                  <Tag color="cautionary">아직</Tag>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between border-line-normal border-t pt-4">
        <span className="text-label-1 text-label-alternative">총액</span>
        <span className="text-heading-1 font-bold text-label-normal tabular-nums">
          {total.toLocaleString("ko-KR")}원
        </span>
      </div>
      {customCount > 0 && (
        <p className="mt-2 text-caption-1 text-label-alternative">
          직접 입력한 주문 {customCount}건은 가격이 없어 총액에 빠져 있어요.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <Button variant="outlined" onClick={handleCopyText}>
          텍스트 복사하기
        </Button>
        {detail.status === "open" ? (
          <Button color="assistive" onClick={() => setConfirmClose(true)}>
            마감하기
          </Button>
        ) : (
          <p className="text-center text-body-2 text-label-alternative">
            {detail.closed_at ? new Date(detail.closed_at).toLocaleString("ko-KR") : ""}에 마감했어요
          </p>
        )}
      </div>

      <Dialog
        open={confirmClose}
        title="마감하기"
        onClose={() => setConfirmClose(false)}
        secondaryAction={
          <Button variant="text" onClick={() => setConfirmClose(false)}>
            취소
          </Button>
        }
        primaryAction={
          <Button onClick={handleClose} disabled={closeOrder.isPending}>
            마감하기
          </Button>
        }
      >
        마감하면 더 이상 주문을 받을 수 없어요. 마감할까요?
      </Dialog>
    </main>
  );
}
